import { faCheck, faRotateLeft } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { FunctionComponent } from 'react';
import styles from './InvoiceStatus.module.scss';

type InvoiceStatusVariant = 'unpaid' | 'partiallyPaid' | 'paid' | 'overpaid' | 'voided' | 'refunded' | 'draft';

const mapInvoiceStatusToVariant = (status: string): InvoiceStatusVariant => {
    switch (status) {
        case 'paid':
            return 'paid';
        case 'overpaid':
            return 'overpaid';
        case 'unpaid':
            return 'unpaid';
        case 'partially paid':
            return 'partiallyPaid';
        case 'void':
            return 'voided';
        case 'refunded':
            return 'refunded';
        case 'draft':
            return 'draft';
        default:
            return 'unpaid';
    }
};

interface InvoiceStatusProps {
    invoiceStatus: string;
    hasRefunds: boolean;
    className?: string;
}

const VARIANT_CONFIG: Record<InvoiceStatusVariant, { label: string; icon?: typeof faCheck }> = {
    unpaid: { label: 'Unpaid' },
    partiallyPaid: { label: 'Partially Paid' },
    paid: { label: 'Paid', icon: faCheck },
    overpaid: { label: 'Overpaid' },
    voided: { label: 'Voided' },
    refunded: { label: 'Refunded', icon: faRotateLeft },
    draft: { label: 'Draft' }
};

export const getInvoiceStatusLabel = (invoiceStatus: string, hasRefunds: boolean): string => {
    const variant = hasRefunds ? 'refunded' : mapInvoiceStatusToVariant(invoiceStatus);
    return VARIANT_CONFIG[variant].label;
};

const VARIANT_CLASS: Record<InvoiceStatusVariant, string> = {
    unpaid: styles.unpaid,
    partiallyPaid: styles.partiallyPaid,
    paid: styles.paid,
    overpaid: styles.overpaid,
    voided: styles.voided,
    refunded: styles.refunded,
    draft: styles.draft
};

export const InvoiceStatus: FunctionComponent<InvoiceStatusProps> = ({ invoiceStatus, hasRefunds, className }) => {
    const variant = hasRefunds ? 'refunded' : mapInvoiceStatusToVariant(invoiceStatus);
    const { label, icon } = VARIANT_CONFIG[variant];

    return (
        <span className={clsx(styles.chip, VARIANT_CLASS[variant], className)}>
            {icon && (
                <FontAwesomeIcon
                    icon={icon}
                    className={styles.icon}
                />
            )}
            {label}
        </span>
    );
};

export default InvoiceStatus;
